import { useCallback, useMemo } from "react";
import { useLocalStorageState } from "../../../../hooks/useLocalStorageState";
import type { HiringSuggestedHire } from "../types";
import {
	type ShiftLength,
	type SuggestedHireDraft,
	shiftsPerFte,
	sortByTemplateOrder,
	type TemplateHours,
	toDraft,
	VALID_SHIFT_LENGTHS,
} from "../utils/suggestedHires";

/**
 * Shape of the people the generator packs the gap into.
 *
 * Which shift types are in scope is deliberately NOT here: the gaps grid already owns a shift filter,
 * and a second copy in this popover meant one page with two controls for one question. The panel now
 * reads that one filter (`HiringModule` derives the exclusion list from it).
 */
export type HireSettings = {
	shiftLength: ShiftLength;
	shiftsPerWeek: number;
	/** How many shift types ONE suggested person may cover. See `HIRE_SHIFT_TYPES`. */
	shiftTypes: HireShiftTypes;
};

/**
 * The wire vocabulary for "may a suggested person cover more than one shift type".
 *
 * Strings, not a numeric cap: they read as themselves in the query string, and they keep the control's
 * value a STRING — which is what makes it safe to pass by value to `SystemFlatSwitch`, whose
 * `activeIndexOrValue` resolves any `number` as an index (see `HireSettingsPopover`).
 */
export const HIRE_SHIFT_TYPES = ["one", "many"] as const;
export type HireShiftTypes = (typeof HIRE_SHIFT_TYPES)[number];

/**
 * ONE shift type per person — and this value is a CONTRACT, not a preference: it must equal the
 * backend's own omitted-parameter default. `storedSettings?.shiftTypes` is `undefined` until the user
 * chooses, so nothing is sent and the server decides; if the two sides disagreed, the popover would
 * show one mode while the suggestions were packed in the other.
 */
export const DEFAULT_HIRE_SHIFT_TYPES: HireShiftTypes = "one";

/**
 * A change to the generation settings — any subset of them.
 *
 * Partial because the popover's three controls each own one field and the caller merges the change
 * over what is stored. Declared once here rather than re-spelt at each of the three places that used
 * to type this callback inline, which is how a fourth field would go missing from one of them.
 */
export type HireGenerationChange = Partial<HireSettings>;

export const SHIFTS_PER_WEEK_MIN = 1;
export const SHIFTS_PER_WEEK_MAX = 7;

/**
 * Two keys, both per-user by construction (each carries the user id, so one manager's decisions never
 * reach another's).
 *
 * They are separate because the generation settings are QUERY INPUTS: they have to be readable before
 * the fetch that produces the suggestions those edits apply to, which one blob can't do.
 */
export const suggestedHiresStorageKey = (userId: string, departmentId: string, jobId: string): string =>
	`hiring-suggested-hires-${userId}-${departmentId}-${jobId}`;
/**
 * Department-scoped, not job-scoped: shift length and shifts-per-week are how this manager thinks about
 * the department's roster as a whole, not about one job's.
 */
export const hireSettingsStorageKey = (userId: string, departmentId: string): string => `hiring-hire-settings-${userId}-${departmentId}`;

/**
 * What the backend generates for when we send no override: the selected job's own shape — its popular
 * shift length and the FTE week that implies. There is no stored department setting behind either.
 */
export const deriveDefaultSettings = (popularShiftHours: number): HireSettings => {
	const shiftLength: ShiftLength = VALID_SHIFT_LENGTHS.includes(popularShiftHours as ShiftLength) ? (popularShiftHours as ShiftLength) : 8;
	return { shiftLength, shiftsPerWeek: shiftsPerFte(shiftLength), shiftTypes: DEFAULT_HIRE_SHIFT_TYPES };
};

export const clampShiftsPerWeek = (value: number): number => Math.min(SHIFTS_PER_WEEK_MAX, Math.max(SHIFTS_PER_WEEK_MIN, Math.round(value)));

/**
 * Persistence for the generation settings. Returns the RAW stored value (`null` when the user has never
 * chosen), so the caller can tell "use the job's own shape / send no query override" from "the user
 * picked these" — and so Revert can put it back to null.
 */
export function useHireSettings(storageKey: string) {
	// The third element REMOVES the key. Writing `null` back would leave the string "null" in storage —
	// a stored value that reads identically to "nothing stored" but never goes away.
	const [storedSettings, setStoredSettings, clearSettings] = useLocalStorageState<HireSettings | null>(storageKey, null);

	/**
	 * THE read boundary for the persisted blob, and it has to be this one.
	 *
	 * Every user who used this popover before `shiftTypes` existed has a two-field blob, and TypeScript
	 * cannot see it: `useLocalStorageState` parses with `as T`, so a partial object type-checks as a
	 * complete `HireSettings` and the hook's try/catch only covers JSON that fails to parse at all.
	 *
	 * Normalizing HERE rather than at a consumer is what repairs it: the caller persists
	 * `{ ...settings, ...next }`, so if `settings` were still the un-normalized blob the missing field
	 * would never be written even when the user changed something else — the partial blob would outlive
	 * every edit.
	 *
	 * A MEMBERSHIP test, not `?? DEFAULT`: that also repairs a value that is present but nonsense (an
	 * older build's spelling, a hand-edited storage entry), which a nullish check would pass straight
	 * through to the query string.
	 *
	 * And deliberately NO write-back. This runs during render, and a write here would both re-enter
	 * storage mid-render and fire the `hiring_hire_settings_changed` analytics event for something the
	 * user did not do. The stored blob repairs itself on their first real change.
	 */
	const normalizedSettings = useMemo<HireSettings | null>(() => {
		// Anything that is not a plain object is "nothing stored". A corrupted or hand-edited entry can
		// hold valid JSON of the wrong SHAPE — `[]`, `[1,2]`, `"hello"` — and each of those spreads into
		// a junk object (`{"0":"h",…}`) with `shiftLength`/`shiftsPerWeek` undefined, which then reads as
		// "the user has edits" and gets persisted back on their next change. Arrays are objects to
		// `typeof`, so they are excluded explicitly.
		if (storedSettings === null || typeof storedSettings !== "object" || Array.isArray(storedSettings)) {
			return null;
		}
		// `unknown` rather than the declared type: the declared type is exactly the thing that lies here.
		const persisted: unknown = storedSettings.shiftTypes;
		const isKnown = typeof persisted === "string" && (HIRE_SHIFT_TYPES as readonly string[]).includes(persisted);
		return isKnown ? storedSettings : { ...storedSettings, shiftTypes: DEFAULT_HIRE_SHIFT_TYPES };
	}, [storedSettings]);

	return { storedSettings: normalizedSettings, setStoredSettings, clearSettings };
}

/**
 * localStorage-backed edit layer over the server's suggestions: a stored list wins, otherwise the freshly
 * generated one renders.
 *
 * Once a stored list exists it is AUTHORITATIVE — a refetch cannot displace it, including the refetch a
 * SHIFT LENGTH / SHIFTS / WEEK change triggers. Only `resetHires` (Revert Changes) hands the page back to
 * the server's suggestions. The earlier model dropped the stored rows on a settings change so the newly
 * generated set could take over; that threw away the user's work, which the PRD asks us to keep.
 *
 * `templateHours` is the response's label → real-hours map. It is threaded in so a row's `shiftLength`
 * pill can be re-derived when the user re-points that row at a different-length template.
 *
 * V2 keeps edits in the browser only — no endpoint, no columns, no migration. The trade-off is stated up
 * front: edits don't follow a user to another device and are lost when site data is cleared. Moving to
 * `user_settings` later is contained to this hook.
 */
export function useSuggestedHireEdits(storageKey: string, serverHires: HiringSuggestedHire[], templateHours: TemplateHours) {
	const [storedHires, setStoredHires, clearStoredHires] = useLocalStorageState<SuggestedHireDraft[] | null>(storageKey, null);

	const serverDrafts = useMemo(() => serverHires.map(toDraft), [serverHires]);
	const hires = storedHires ?? serverDrafts;
	const hasEdits = storedHires !== null;

	// Every mutation materialises the server's list first, so the first edit forks it into storage.
	const mutateHires = useCallback(
		(mutate: (current: SuggestedHireDraft[]) => SuggestedHireDraft[]) => {
			setStoredHires((prev) => mutate(prev ?? serverDrafts));
		},
		[setStoredHires, serverDrafts],
	);

	const updateHire = useCallback(
		(id: string, update: (hire: SuggestedHireDraft) => SuggestedHireDraft) => {
			mutateHires((current) => current.map((hire) => (hire.id === id ? update(hire) : hire)));
		},
		[mutateHires],
	);

	/**
	 * A rename makes the name the USER's. It is stored as `customName` and never derived over again — not
	 * when they toggle a day, not when they change `SHIFTS / WEEK`, not when the server regenerates.
	 */
	const renameHire = useCallback((id: string, customName: string) => updateHire(id, (hire) => ({ ...hire, customName })), [updateHire]);

	const setHireShifts = useCallback(
		(id: string, labels: string[], templateOrder: readonly string[], daysToAdd: Record<number, string> = {}) => {
			// Deselecting the last shift type is a no-op: a person with no shift can't cover anything.
			if (labels.length === 0) {
				return;
			}
			const ordered = sortByTemplateOrder(labels, templateOrder);
			updateHire(id, (hire) => {
				/**
				 * Two different edits wear the same control, and they need opposite handling.
				 *
				 * NARROWING — the new selection still contains something they worked (`[A,B]` → `[A]`): the
				 * dropped shift's days GO. Relabelling them onto the kept shift left the day chip lit while
				 * silently changing what it meant, so the row looked untouched and only its gap dot moved —
				 * which is what made un-ticking a shift look like it had done nothing. It also invented
				 * coverage: that day was short on the shift being removed, not on the one inheriting it.
				 *
				 * RE-POINTING — the new selection shares nothing with the old (`[A]` → `[B]`): the days MOVE.
				 * "This person works nights instead" should keep the week that was built for them; dropping
				 * every day would silently throw the edit away and leave an empty row.
				 */
				const worked = new Set(Object.values(hire.shiftByDay));
				const isRepoint = worked.size > 0 && !ordered.some((label) => worked.has(label));
				const shiftByDay: Record<number, string> = {};
				for (const [key, label] of Object.entries(hire.shiftByDay)) {
					if (ordered.includes(label)) {
						shiftByDay[Number(key)] = label;
					} else if (isRepoint) {
						shiftByDay[Number(key)] = ordered[0];
					}
				}
				/**
				 * WIDENING brings work with it. Ticking a second shift type used to change only the pill and
				 * the gap dots — the person covered the new shift on paper and worked none of it, so the edit
				 * read as inert. The caller resolves WHICH days (it owns the Missing grid and the
				 * shifts-per-week cap); this only merges them, and never over a day already spoken for.
				 */
				for (const [key, label] of Object.entries(daysToAdd)) {
					if (ordered.includes(label) && shiftByDay[Number(key)] === undefined) {
						shiftByDay[Number(key)] = label;
					}
				}
				/**
				 * The hours pill has to follow the row's CURRENT templates: a row re-pointed from `12am-8am`
				 * to a 12-hour template must stop reading `8H`.
				 *
				 * Multi-template rule: the pill takes the length of the row's FIRST covered shift in
				 * template order. The pill is a single label, and this is exactly what `shiftLength` already
				 * means on the wire — `shiftLength` names the template a person STARTS on, so a row covering
				 * one template is unchanged by this rule and a spanning row (a user edit, or a suggestion
				 * generated with SHIFT TYPES PER PERSON set to "multiple") reads as its earliest template.
				 * Deliberately NOT an average or a sum: the employment badge is a shift count over the
				 * SHIFTS / WEEK setting and never reads this field, so a synthesised number would be a
				 * label no template actually has.
				 */
				const firstCoveredLength = templateHours[ordered[0]];
				// DEFAULT: `templateHours` is the response's own map of every template label, so a label the
				// user can pick is always present. Should a payload ever omit one, the row keeps its last
				// real length — a stale pill beats rendering `undefinedH`.
				return { ...hire, coverShifts: ordered, shiftLength: firstCoveredLength ?? hire.shiftLength, shiftByDay };
			});
		},
		[updateHire, templateHours],
	);

	/** Clearing an on day removes it; adding an off day needs the shift label the caller resolved. */
	const setDay = useCallback(
		(id: string, key: number, label: string | null) => {
			updateHire(id, (hire) => {
				const shiftByDay = { ...hire.shiftByDay };
				if (label === null) {
					delete shiftByDay[key];
				} else {
					shiftByDay[key] = label;
				}
				return { ...hire, shiftByDay };
			});
		},
		[updateHire],
	);

	const setApplied = useCallback((id: string, applied: boolean) => updateHire(id, (hire) => ({ ...hire, applied })), [updateHire]);

	const removeHire = useCallback((id: string) => mutateHires((current) => current.filter((hire) => hire.id !== id)), [mutateHires]);

	/**
	 * A row the user built themselves.
	 *
	 * `name` is OPTIONAL, and that distinction is the point: a name they typed becomes `customName` and
	 * is never touched again, while leaving the field blank leaves the row unnamed so it is DERIVED like
	 * a generated one — which is also what numbers two otherwise-identical manual hires apart. Filling in
	 * a placeholder here instead ("New hire") made both rows read the same and neither renumberable,
	 * because the naming rule refuses to renumber a name the user chose — and they had not chosen it, we
	 * had.
	 *
	 * The name, the starting shift label and the days are all caller-owned, so this layer holds no copy
	 * and no template knowledge.
	 */
	const addHire = useCallback(
		(input: { name?: string; shiftLabel: string; shiftLength: number; shiftByDay: Record<number, string>; applied: boolean }) => {
			mutateHires((current) => [
				...current,
				{
					id: `manual-${Date.now()}`,
					// Only a typed name; `undefined` leaves the row derived and numbered like a generated one.
					customName: input.name?.trim() === "" ? undefined : input.name?.trim(),
					shiftByDay: input.shiftByDay,
					coverShifts: [input.shiftLabel],
					shiftLength: input.shiftLength,
					// The dialog's button is "Save & Apply", so a manual hire arrives already counting as
					// coverage — which is the already-hired-but-not-in-the-template case it exists for.
					applied: input.applied,
				},
			]);
		},
		[mutateHires],
	);

	/**
	 * Drop every stored edit so the server's freshly generated suggestions render again. A key DELETION,
	 * not a `null` write: writing the initial value back stores the literal string "null", which reads
	 * identically to "nothing stored" while leaving the key behind forever.
	 */
	const resetHires = clearStoredHires;

	return { hires, hasEdits, renameHire, setHireShifts, setDay, setApplied, removeHire, addHire, resetHires };
}
